import { Component, OnInit } from '@angular/core';
import { ClearAccountCache, GetAccount } from '@app/finxone-web-frontend/app/lib/actions/account.action';
import {
  ClearSavingAccountCache,
  GetSavingAccount,
} from '@app/finxone-web-frontend/app/lib/actions/savings-account.action';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { AccountState, AccountStateModel } from '@app/finxone-web-frontend/app/lib/state/account.state';
import { RoleState, RoleStateModel } from '@app/finxone-web-frontend/app/lib/state/role.state';
import {
  SavingsAccountState,
  SavingsAccountStateModel,
} from '@app/finxone-web-frontend/app/lib/state/savings-account.state';
import {
  createRedirectionUrlFromNavigationOptions,
  removeDuplicateCurrencies,
  removeInvalidCurrencies,
} from '@app/finxone-web-frontend/app/lib/utils/utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  AccountStatusType,
  AccountTypes,
  DummyRole,
  NavigationOptionEnum,
  NavigationOptionsType,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  ViewType,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, map, takeUntil } from 'rxjs';
import { BaseAccountsListingComponent } from '../base/base-accounts-listing.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-accounts-listing-widget',
  templateUrl: './accounts-listing-widget.component.html',
  styleUrl: './accounts-listing-widget.component.scss',
})
export class AccountsListingWidgetComponent extends BaseAccountsListingComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Select(AccountState.getAccounts)
  private accountState$!: Observable<AccountStateModel>;

  @Select(SavingsAccountState.getAccounts)
  public savingsAccounts$!: Observable<SavingsAccountStateModel>;

  @Select(RoleState.getRole)
  private role$!: Observable<RoleStateModel>;
  public accountContextStatusList: AccountStatusType[];

  constructor(
    public override dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
    public override store: Store,
    public override configService: ConfigService,
  ) {
    super(dataIn, store, configService);
    if (this.dataIn?.attri) {
      this.widgetConfig = this.dataIn.attri.widgetProperties;
      this.contextSwitch = this.dataIn.attri['contextSwitch'];
      this.accountContextStatusList = this.dataIn.attri?.['accountContextList']?.map(
        (item: { id: AccountStatusType }) => item.id,
      ) ?? [AccountStatusType.active];

      this.setNavigation();
    }

    this.getWidgetCssConfig();
    this.configService
      .getCurrencies()
      .pipe(
        takeUntil(this.destroy$),
        map((response) => {
          const validCurrencies = removeInvalidCurrencies(response);
          this.currencies = removeDuplicateCurrencies(validCurrencies, 'currency_code');
        }),
      )
      .subscribe();
  }

  ngOnInit(): void {
    if (this.context === AccountTypes.SAVINGS_ACCOUNT) {
      this.store.dispatch(new ClearSavingAccountCache());
      this.store.dispatch(
        new GetSavingAccount(this.pageNumber, this.pageSize, this.accountContextStatusList),
      );
      this.savingsAccounts$
        .pipe(
          map((response) => {
            this.accountsInfo = response;
            this.totalPages = response.accountTotalPages;
          }),
        )
        .subscribe();
    } else {
      this.store.dispatch(new ClearAccountCache());
      this.store.dispatch(new GetAccount(this.pageNumber, this.pageSize, this.accountContextStatusList));
      this.accountState$
        .pipe(
          map((response) => {
            this.accountsInfo = response;
            this.totalPages = response.accountTotalPages;
          }),
        )
        .subscribe();
    }

    this.configService.getCountries().subscribe({
      next: (data) => {
        this.countries = data;
      },
    });
  }

  private setNavigation() {
    if (this.dataIn?.attri['role'] === DummyRole.EMPTY_ROLE) {
      this.role$
        .subscribe((roleInfo) => {
          this.dataIn.attri['role'] = roleInfo.role;
        })
        .unsubscribe();
    }

    if (this.dataIn.attri['navigationOption'] !== NavigationOptionEnum.USE_AS_BLANK) {
      const navConfig: NavigationOptionsType = {
        navigationOption: this.dataIn.attri['navigationOption'],
        zone: this.dataIn.attri['zone'],
        pageUrl: this.dataIn.attri['pageUrl'],
        pageId: this.dataIn.attri['pageId'],
        pageTransition: this.dataIn.attri['pageTransition'],
        role: this.dataIn.attri['role'],
        tabRef: this.dataIn.attri['tabRef'],
        externalUrl: this.dataIn.attri['externalUrl'],
      };
      this.navigateToUrl = createRedirectionUrlFromNavigationOptions(navConfig);
    }
  }

  public handleChildComponentScroll() {
    const selector = this.contextBasedLoadingSelectors[this.context];
    if (!this.store.selectSnapshot(selector)) {
      this.handleChildScroll({
        accountContextStatusList: this.accountContextStatusList,
        searchTerm: undefined,
        viewMode: this.dataIn.attri?.widgetProperties?.['viewType'] ?? ViewType.LIST,
        pageSize: this.pageSize,
      });
    }
  }

  public handleTablePaginationClickAndSearch(event: {
    accountContextStatusList: AccountStatusType[];
    searchTerm: { name: string } | undefined;
    viewMode: ViewType;
    pageSize: number;
    page?: number;
  }) {
    this.handleChildScroll({
      ...event,
      accountContextStatusList: this.accountContextStatusList,
    });
  }
}
